import { platformBrowserDynamic } from '@angular/platform-browser-dynamic'

import { AppModule } from './app/app.module'
import { environment } from './environments/environment';
import { enableProdMode } from '@angular/core';
import * as Sentry from "@sentry/angular";

if(environment.sourceName.toLocaleLowerCase().indexOf('local') < 0){
  console.log = function () { };
  console.warn = function () { };
  console.info = function () { };
}
if (environment.production) {
  // disable console logs
  enableProdMode();
}

// Detect stale-chunk errors caused by a deploy changing chunk hashes while the user
function isChunkLoadError(message: string | undefined): boolean {
  if (!message) return false;
  return message.includes('Loading chunk')
    || message.includes('error loading dynamically imported module')
    || message.includes('Failed to fetch dynamically imported module');
}

const CHUNK_RELOAD_COOLDOWN_MS = 30_000;
window.addEventListener('unhandledrejection', event => {
  if (!isChunkLoadError(event.reason?.message)) return;

  const lastReload = Number(sessionStorage.getItem('chunkLoadReloadAt') ?? 0);
  if (Date.now() - lastReload < CHUNK_RELOAD_COOLDOWN_MS) return;

  sessionStorage.setItem('chunkLoadReloadAt', String(Date.now()));
  window.location.reload();
});

Sentry.init({
  dsn: environment.sentryTokenUrl,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,

  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false
    })
  ],
  // Session Replay
  replaysSessionSampleRate: environment.production ? 1.0 : 0.1, // Record 100% of all sessions in production, 10% otherwise
  replaysOnErrorSampleRate: 1.0, // Record 100% of sessions containing errors

  environment: environment.sourceName,

  beforeSend(event, hint) {
    const error: any = hint?.originalException;
    const message = error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
    const stack = error instanceof Error ? error.stack ?? '' : '';

    // suppress primeNG-17 specific android error which is not actionable
    if (
      message.includes("reading 'begin'") &&
      (stack.includes('primeng') || stack.includes('handleAndroidInput'))
    ) {
      return null;
    }

    // Suppress stale-chunk errors, the rejection handler above reloads the page to recover,
    if (isChunkLoadError(message)) {
      return null;
    }

    // Suppress network / offline errors
    if (
      error?.status === 0 ||
      message?.includes('not connected to the internet') ||
      message?.includes('HTTP 0') ||
      event.exception?.values?.some(v =>
        v.value?.includes('not connected to the internet')
      )
    ) {
      return null; // Drop event
    }

    return event; // Allow everything else
  }
});

platformBrowserDynamic().bootstrapModule(AppModule)
  .catch(err => console.error(err))
